import {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteMeta,
  RouteRecordRaw,
  useRouter as vueUseRouter,
} from 'vue-router';
import { Store } from 'vuex';

export const RouteNames = {
  ACCOUNT: {
    INDEX: 'account',
    SETTINGS: 'account-settings',
  },
  AUTH: {
    LOGIN: 'login',
    LOGOUT: 'logout',
    SIGNUP: 'payments-signup',
    PASSWORD_RESET: 'password-reset',
    ERROR: 'auth-error',
    LINK_ACCOUNTS: 'link-accounts',
  },
  BANKING: {
    INDEX: 'payments-profile/connect-banking',
    STATEMENTS: 'payments-profile/bank-statements',
    STATEMENTS_LEGACY: 'statements-legacy',
  },
  BUSINESS: {
    ADDRESS: 'payments-profile/registered-address',
    INDEX: 'payments-profile/business-details',
  },
  CONNECTED_ACCOUNTS: {
    INDEX: 'payments-profile/verify-account',
    MARKETING: 'payments-profile/connect-marketing',
  },
  DATA_SECURITY: {
    INDEX: 'data-security',
    REMOVE: 'remove-data',
    EXPORT: 'export-data',
  },
  DEBT_OBLIGATIONS: {
    INDEX: 'payments-profile/debt-obligations',
  },
  DOCUMENTS: {
    INDEX: 'pay-vendors/all-documents',
  },
  GCS_DOWNLOAD_PROXY: {
    INDEX: 'gcs-download-proxy',
  },
  EMAIL_VERIFICATION: {
    INDEX: 'email-verification',
  },
  EMBED: {
    CALCULATOR: 'calculator',
    INDEX: 'embed',
  },
  FINANCIAL: {
    DOCUMENTS: 'payments-profile/bank-documents',
  },
  HOME: {
    INDEX: 'home',
    INITIAL: 'initial-home',
  },
  HOW_IT_WORKS: {
    INDEX: 'how-it-works',
  },
  MAINTENANCE: {
    INDEX: 'maintenance',
  },
  MCA: {
    VENDOR_LIST: 'vendor-list',
    CLEARCO_CARD: 'clearco-card',
    PAYMENTS: 'payments',
    SPEND_TRANSACTIONS: 'spend-transactions',
    INDEX: 'financing',
    SPEND: 'spend',
    ADVANCES: 'advances',
    APPLICATION: 'application',
  },
  NOT_FOUND: {
    INDEX: 'not-found',
  },
  OAUTH: {
    TRIGGER: 'oauth-trigger',
    SIGNUP: 'oauth-signup',
    CALLBACK: 'oauth-callback',
    PLAID_CALLBACK: 'plaid-oauth-callback',
  },
  ONBOARDING: {
    INDEX: 'payments-onboarding',
    USER: 'payments-user-profile',
    BUSINESS: 'payments-business-details',
    ADDRESS: 'payments-address',
    REVENUE_ACCOUNT: 'payments-revenue-account',
    INVITED: {
      INDEX: 'payments-invited-user-onboarding',
      USER_DETAILS: 'payments-invited-user-details',
      VERIFICATION: 'payments-invited-user-verification',
      ID: 'payments-invited-user-id',
      REVENUE_ACCOUNT: 'payments-invited-user-revenue-account',
    },
  },
  OWNERS: {
    EDIT: 'payments-profile/edit-owner',
    INDEX: 'payments-profile/owners-list',
    NEW: 'payments-profile/new-owner',
  },
  PAY_VENDORS: {
    INDEX: 'pay-vendors',
  },
  PAYMENTS: {
    INDEX: 'pay-vendors/repayments',
    UPCOMING: 'pay-vendors/upcoming-repayments',
  },
  PROFILE: {
    INDEX: 'payments-profile',
  },
  TEAM: {
    INDEX: 'team',
  },
  USER: {
    INDEX: 'payments-profile/owners-step-one',
  },
  VERIFICATION: {
    VERIFYING: 'profile/verification/verifying',
    COMPLETE: 'profile/verification/complete',
    INFO: 'profile/verification/info',
    DOCUMENTS: 'profile/verification/documents',
  },
  LEGAL: {
    NON_PERMITTED_ACTIVITY: 'legal/non-permitted-activity',
  },
} as const;

type FlattenRouteNames<T> = T extends object
  ? {
      [K in keyof T]: T[K] extends string ? T[K] : FlattenRouteNames<T[K]>;
    }[keyof T]
  : T extends string
    ? T
    : never;

export type RouteName = FlattenRouteNames<typeof RouteNames>;

type HasAccessType = { store?: Store<any>; to?: RouteLocationNormalized };

export interface CustomRouteMeta extends RouteMeta {
  requiresAuth?: boolean;
  displayNav?: boolean;
  segment?: string[];
  hasAccess?: (params: HasAccessType) => boolean;
  featureFlags?: string[];
  fallbackRoute?: RouteName;
}

export type TypedRouteRecordRaw = Omit<RouteRecordRaw, 'name' | 'children'> & {
  name?: RouteName;
  children?: TypedRouteRecordRaw[];
  meta?: CustomRouteMeta;
};

export type TypedRouteLocationRaw = Omit<
  RouteLocationRaw,
  'name' | 'params'
> & {
  name?: RouteName;
  params?: Record<string, any>;
  meta?: CustomRouteMeta;
};

export interface TypedRouter {
  push(
    to: RouteName | TypedRouteLocationRaw,
  ): ReturnType<ReturnType<typeof vueUseRouter>['push']>;
  replace(
    to: RouteName | TypedRouteLocationRaw,
  ): ReturnType<ReturnType<typeof vueUseRouter>['push']>;

  back(): void;
  go(delta: number): void;

  currentRoute: ReturnType<typeof vueUseRouter>['currentRoute'];
}
